// src/utils/api.js — ESQUEMA AJUSTADO (con status derivado correcto y prioridad real)
// Convención: estas funciones LANZAN errores (throw). Usar try/catch en componentes.

import { supabase } from './supabase';
import { findInstrumentDuplicates } from './instrumentDuplicateGuard';

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────

// Obtiene user actual (útil para setear uploaded_by)
async function ensureUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data?.user) throw new Error('No hay usuario activo');
  return data.user;
}

// Mantengo esta función para otros módulos, pero el grid ahora usa deriveStatus() (más abajo)
function deriveStatusFromDates(nextCalDate) {
  const now = new Date();
  const due = nextCalDate ? new Date(nextCalDate) : null;
  if (!due) return { status: 'pending', label: 'Pendiente' };
  if (due < now) return { status: 'overdue', label: 'Vencida' };
  // Antes devolvía "in-progress"; lo dejo por compatibilidad EN OTROS USOS,
  // pero el Grid usará deriveStatus() que considera calibraciones abiertas.
  return { status: 'in-progress', label: 'En curso' };
}

// Cálculo de errores de metrología "on the fly"
function computeErrors({ setpoint, reading }) {
  const sp = Number(setpoint);
  const rd = Number(reading);
  const abs_error = rd - sp;
  const rel_error = sp !== 0 ? abs_error / sp : null; // proporción (no %)
  return { abs_error, rel_error };
}

// Fallback: sumar meses a una fecha (ISO out) para calcular vencimiento si falta
function addMonths(dateLike, months) {
  if (!dateLike || months == null) return null;
  const d0 = new Date(dateLike);
  if (isNaN(d0.getTime())) return null;
  const d = new Date(d0.getTime());
  d.setMonth(d.getMonth() + Number(months || 0));
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// Bucket helpers (month/week/day)
function bucketKey(d, mode) {
  const dt = new Date(d);
  if (mode === 'day') return dt.toISOString().slice(0, 10); // YYYY-MM-DD
  if (mode === 'week') {
    const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    const dayNum = (tmp.getUTCDay() + 6) % 7;
    tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
    const firstThursday = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((tmp - firstThursday) / 86400000 - 3) / 7);
    return `${dt.getFullYear()}-W${String(week).padStart(2, '0')}`;
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

// ───────────────────────────────────────────────────────────────────────────────
// Auth
// ───────────────────────────────────────────────────────────────────────────────

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function signIn({ email, password }) {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: normalizedEmail,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  return true;
}

// ───────────────────────────────────────────────────────────────────────────────
// Clients
// ───────────────────────────────────────────────────────────────────────────────

export async function createClient(payload) {
  const user = await ensureUser();

  // Convertir vacíos a null y asegurar tipo numérico
  const client_number =
    payload.client_number === "" || payload.client_number == null
      ? null
      : Number(payload.client_number);

  const branch_no =
    payload.branch_no === "" || payload.branch_no == null
      ? null
      : Number(payload.branch_no);

  // Armar payload con trims y owner_id
  const insertPayload = {
    owner_id: user.id,
    name: payload.name?.trim(),
    cuit: payload.cuit?.trim() || null,
    tax_id: payload.tax_id?.trim() || null,
    contact: payload.contact?.trim() || null,
    email: payload.email?.trim() || null,
    phone: payload.phone?.trim() || null,
    address: payload.address?.trim() || null,
    locality: payload.locality?.trim() || null,   // ✅ FIX: se omitían al crear
    province: payload.province?.trim() || null,    // ✅ FIX
    branch_name: payload.branch_name?.trim() || null,
    client_number,
    branch_no,
  };

  // Mantener consistencia CUIT / tax_id (si viene uno y no el otro)
  if (!insertPayload.cuit && insertPayload.tax_id) insertPayload.cuit = insertPayload.tax_id;
  if (!insertPayload.tax_id && insertPayload.cuit) insertPayload.tax_id = insertPayload.cuit;

  const { data, error } = await supabase
    .from("clients")
    .insert(insertPayload)
    .select(`
      id,
      name,
      cuit,
      tax_id,
      contact,
      email,
      phone,
      address,
      locality,
      province,
      client_number,
      branch_no,
      branch_name,
      created_at
    `)
    .single();

  if (error) throw error;
  return data;
}

export async function searchClients(query) {
  const q = String(query || "").trim();
  if (!q) return [];

  const baseSelect = `
    id,
    name,
    cuit,
    tax_id,
    contact,
    email,
    phone,
    address,
    client_number,
    branch_no,
    branch_name,
    created_at
  `;

  // formato "1088 1"
  const m = q.match(/^(\d+)\s+(\d+)$/);
  if (m) {
    const cn = Number(m[1]);
    const bn = Number(m[2]);
    const { data, error } = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("client_number", cn)
      .eq("branch_no", bn)
      .limit(50);

    if (error) throw error;
    return data || [];
  }

  // solo número "1088" => todas sus sucursales
  if (/^\d+$/.test(q)) {
    const cn = Number(q);
    const { data, error } = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("client_number", cn)
      .order("branch_no", { ascending: true })
      .limit(50);

    if (error) throw error;
    return data || [];
  }

  // texto => name/address/cuit/tax_id
  const like = `%${q}%`;
  const { data, error } = await supabase
    .from("clients")
    .select(baseSelect)
    .or(
      [
        `name.ilike.${like}`,
        `address.ilike.${like}`,
        `cuit.ilike.${like}`,
        `tax_id.ilike.${like}`,
      ].join(",")
    )
    .order("client_number", { ascending: true })
    .order("branch_no", { ascending: true })
    .limit(50);

  if (error) throw error;
  return data || [];
}

export async function updateClient(clientId, payload) {
  const clean = { ...(payload || {}) };

  // mantener CUIT / tax_id consistentes
  if (clean.cuit == null && clean.tax_id) clean.cuit = clean.tax_id;
  if (clean.tax_id == null && clean.cuit) clean.tax_id = clean.cuit;

  const { data, error } = await supabase
    .from("clients")
    .update(clean)
    .eq("id", clientId)
    .select(`
      id,
      name,
      cuit,
      tax_id,
      contact,
      email,
      phone,
      address,
      locality,
      province,
      client_number,
      branch_no,
      branch_name,
      created_at
    `)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Cambiar client_number/branch_no de forma segura.
 * Recomendado: RPC en BD (SECURITY DEFINER) para validar permisos + evitar duplicados.
 */
export async function secureUpdateClientCode({ client_id, new_client_number, new_branch_no, reason }) {
  const { data, error } = await supabase.rpc("secure_update_client_code", {
    p_client_id: client_id,
    p_client_number: new_client_number,
    p_branch_no: new_branch_no,
    p_reason: reason,
  });

  if (error) throw error;

  // Si el RPC retorna el cliente, lo devolvemos.
  const row = Array.isArray(data) ? data[0] : data;
  if (row && typeof row === "object" && (row.id || row.client_number != null || row.branch_no != null)) {
    return row;
  }

  // ✅ Fallback: aunque el RPC retorne null/void/ok, traemos el cliente actualizado
  const { data: fresh, error: e2 } = await supabase
    .from("clients")
    .select(`
      id,
      name,
      cuit,
      tax_id,
      contact,
      email,
      phone,
      address,
      client_number,
      branch_no,
      branch_name,
      billing_email_1,
      billing_email_2,
      created_at
    `)
    .eq("id", client_id)
    .single();

  if (e2) throw e2;
  return fresh;
}

export async function listClients() {
  const user = await ensureUser();
  const { data, error } = await supabase
    .from('clients')
    .select('id, name, contact, email, phone, address, tax_id, created_at')
    .eq('owner_id', user.id)
    .order('name', { ascending: true });

  if (error) throw error;
  return data;
}

export async function getNextClientNumber() {
  const { data, error } = await supabase.rpc("get_next_client_number");
  if (error) throw error;
  return data;
}

// ───────────────────────────────────────────────────────────────────────────────
// Instruments
// ───────────────────────────────────────────────────────────────────────────────
//
// FK confirmada: instruments_client_id_fkey

export async function createInstrument(payload) {
  const user = await ensureUser();
  console.log('[createInstrument] Usuario autenticado:', user.id);
  const toDateOnly = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    const direct = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (direct) return raw.slice(0, 10);

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const row = {
    owner_id: user.id,
    client_id: payload.client || null,
    tag: payload.tag || null,
    brand: payload.brand || null,
    model: payload.model || null,
    serial: payload.serialNumber || null,
    type: payload.type || 'pressure',
    range_min: payload.range?.min ? Number(String(payload.range.min).replace(',', '.')) : null,
    range_max: payload.range?.max ? Number(String(payload.range.max).replace(',', '.')) : null,
    unit: payload.range?.unit || null,
    resolution: payload.resolution
      ? Number(String(payload.resolution).replace(',', '.'))
      : null,
    tolerance: payload.tolerance?.value
      ? Number(String(payload.tolerance.value).replace(',', '.'))
      : null,
    calibrationintervalmonths: payload.calibrationIntervalMonths
      ? parseInt(payload.calibrationIntervalMonths, 10)
      : null,
    lastcalibrationdate: payload.intakeDate ? new Date(payload.intakeDate).toISOString() : null,
    reception_date: toDateOnly(payload.receptionDate || payload.intakeDate) || undefined,
    calibration_place: payload.calibrationPlace || 'Laboratorio',
    status: 'active',
    created_at: new Date().toISOString(),
  };
  console.log('[createInstrument] Payload transformado:', row);
  const duplicates = await findInstrumentDuplicates(supabase, row);
  if (duplicates.length > 0) {
    const duplicate = duplicates[0];
    const err = new Error(
      `Ya existe un instrumento similar para este cliente: Lab ID ${duplicate.lab_id || "-"}`
    );
    err.code = "INSTRUMENT_DUPLICATE";
    err.existingInstrument = duplicate;
    throw err;
  }

  const { data, error } = await supabase
    .from('instruments')
    .insert(row)
    .select(`
      id, owner_id, client_id, tag, brand, model, type, unit, serial,
      range_min, range_max, resolution, tolerance,
      lastcalibrationdate, nextcalibrationdate, calibrationintervalmonths, 
      status, created_at,
      client:clients!instruments_client_id_fkey ( id, name )
    `)
    .single();
  if (error) {
    console.error('[createInstrument] ERROR completo:', JSON.stringify(error, null, 2));
    throw error;
  }
  console.log('[createInstrument] Instrumento creado:', data);
  // Verificación adicional: buscar directamente por ID
  const { data: verify, error: verifyError } = await supabase
    .from('instruments')
    .select('*')
    .eq('id', data.id)
    .single();
  console.log('[createInstrument] Verificación directa:', { verify, verifyError });
  return data;
}

export async function listInstruments({ clientId, from = 0, to = 49 } = {}) {
  const user = await ensureUser();
  let q = supabase
    .from('instruments')
    .select(
      'id, owner_id, client_id, tag, brand, model, type, unit, serial, ' +
      'lastcalibrationdate, nextcalibrationdate, calibrationintervalmonths, created_at,' +
      'client:clients!instruments_client_id_fkey ( id, name )',
      { count: 'exact' }
    )
    .eq('owner_id', user.id)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (clientId && clientId !== 'all') q = q.eq('client_id', clientId);

  const { data, error, count } = await q;
  if (error) throw error;
  return { data, count };
}

export async function getInstrument(id) {
  const { data, error } = await supabase
    .from('instruments')
    .select(`
      id, lab_id, tag, brand, model, serial, type, unit,
      baker_code, observations, calibration_place,
      range_min, range_max, resolution, tolerance,
      status,
      reception_date,
      departure_date,
      lastcalibrationdate, nextcalibrationdate, calibrationintervalmonths,
      certificatenumber,
      qr_image_url, qr_pdf_url,
      technician_notes, rejection_reason, is_broken, repair_requested,
      lab_entry_date, reviewed_by, reviewed_at,
      category:instrument_categories ( id, name ),
      client:clients (
        id, name, contact, email, phone, address, tax_id
      )
    `)
    .eq('id', id)
    .single();

  if (error) throw error;

  // IMPORTANTE: devolver snake_case tal cual, sin mapear a camelCase aquí.
  return data;
}

export async function deleteInstrument(instrumentId) {
  if (!instrumentId) throw new Error('Falta instrumentId');

  // RLS obliga a que owner_id = auth.uid(), así que
  // sólo podrás borrar instrumentos propios.
  const { error } = await supabase
    .from('instruments')
    .delete()
    .eq('id', instrumentId);

  if (error) {
    console.error('[deleteInstrument] Supabase error:', error);
    throw new Error(error.message || 'No se pudo borrar el instrumento');
  }
  return true;
}

// ───────────────────────────────────────────────────────────────────────────────
// Instrument Attachments
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Sube adjuntos al bucket 'instrument-attachments' en la ruta:
 * instruments/<instrumentId>/<filename>
 * Devuelve los metadatos insertados (si usas la tabla instrument_files) o un arreglo con paths.
 */
export async function uploadInstrumentAttachments(instrumentId, attachments) {
  if (!instrumentId || !Array.isArray(attachments) || attachments.length === 0) return [];
  const user = await ensureUser();
  const bucket = 'instrument-attachments';

  const uploaded = [];
  for (const att of attachments) {
    const file = att?.file || att; // por si viene directo como File
    const safeName = String(file?.name || `file_${Date.now()}`)
      .replace(/\s+/g, '_')
      .replace(/[^\w.\-]/g, '');
    const path = `instruments/${instrumentId}/${Date.now()}_${safeName}`;

    const { error: upErr } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file?.type || undefined,
      });

    if (upErr) throw upErr;

    uploaded.push({
      instrument_id: instrumentId,
      path,
      name: file?.name || safeName,
      mime: file?.type || null,
      size: file?.size || null,
      uploaded_by: user.id,
    });
  }

  // (Opcional) guarda metadatos
  const { data, error } = await supabase
    .from('instrument_files')
    .insert(uploaded)
    .select();

  if (error) throw error;

  return data || uploaded.map(u => ({ path: u.path, name: u.name }));
}

/** Obtiene URL pública (si el bucket es público) o firmada */
export function getPublicUrl(path, expiresInSec = 3600) {
  const bucket = 'instrument-attachments';
  const pub = supabase.storage.from(bucket).getPublicUrl(path);
  if (pub?.data?.publicUrl) return pub.data.publicUrl;
  // si no es público, firma la URL
  return supabase.storage.from(bucket).createSignedUrl(path, expiresInSec)
    .then(({ data }) => data?.signedUrl || null);
}


// ───────────────────────────────────────────────────────────────────────────────
// Calibrations
// ───────────────────────────────────────────────────────────────────────────────

export async function createCalibration(payload) {
  await ensureUser();
  const { data, error } = await supabase
    .from('calibrations')
    .insert(payload) // { instrument_id, date?, technician_id?, ambient_*?, procedure_version? }
    .select('id, instrument_id, date, technician_id, ambient_temp, ambient_humidity, procedure_version, status, priority')
    .single();

  if (error) throw error;
  return data;
}

// 1) listCalibrations — incluir technician_name y certificate_url
export async function listCalibrations() {
  await ensureUser();
  const { data, error } = await supabase
    .from('calibrations')
    .select(`
      id,
      date,
      status,
      priority,
      technician_id,
      technician_name,
      certificate_url,
      certificate_created_at,
      instrument:instrument_id (
        id,
        tag,
        brand,
        model,
        serial,
        type,
        unit,
        range_min,
        range_max,
        resolution,
        tolerance,
        owner_id,
        status,
        lastcalibrationdate,
        nextcalibrationdate,
        calibrationintervalmonths,
        certificatenumber,
        created_at,
        client:client_id (
          id,
          name,
          tax_id,
          contact,
          email,
          phone,
          address
        )
      )
    `)
    .order('date', { ascending: false });

  if (error) throw error;
  return data || [];
}

// 🔹 Necesario para MeasurementEditor / navegación
export async function getCalibrationById(calibrationId) {
  await ensureUser();
  const { data, error } = await supabase
    .from('calibrations')
    .select('id, instrument_id, date, technician_id, ambient_temp, ambient_humidity, procedure_version, status, priority')
    .eq('id', calibrationId)
    .single();
  if (error) throw error;
  return data;
}

// Compatibilidad histórica
export async function updateCalibrationPriority(/* id, priorityUi */) {
  await ensureUser();
  return { ok: true };
}

// 🔹 Usado por WorkOrderGrid
export async function setCalibrationPriority(calibrationId, priority) {
  if (!calibrationId) throw new Error('calibrationId es requerido');
  if (!['low','medium','high'].includes(String(priority))) {
    throw new Error('priority inválida (usa: low | medium | high)');
  }
  const { error } = await supabase
    .from('calibrations')
    .update({ priority })
    .eq('id', calibrationId);
  if (error) throw error;
  return { ok: true };
}

/**
 * Cierra una calibración actualizando las fechas del instrumento:
 * - lastcalibrationdate = ahora
 * - nextcalibrationdate = ahora + calibrationintervalmonths (si existe)
 * (si quieres, aquí también podríamos marcar status='closed' en la calibración)
 */
export async function closeCalibration({ calibrationId, instrumentId }) {
  await ensureUser();
  if (!instrumentId) throw new Error('Falta instrumentId');

  // obtener instrumento para conocer el intervalo de meses
  const inst = await getInstrument(instrumentId);
  if (!inst) throw new Error('Instrumento no encontrado');

  const nowIso = new Date().toISOString();
  const nextIso =
    inst.calibrationintervalmonths
      ? addMonths(nowIso, inst.calibrationintervalmonths)
      : null;

  const { error } = await supabase
    .from('instruments')
    .update({
      lastcalibrationdate: nowIso,
      nextcalibrationdate: nextIso
    })
    .eq('id', instrumentId);

  if (error) throw error;

  // (opcional) marcar la calibración como cerrada
  // await supabase.from('calibrations').update({ status: 'closed' }).eq('id', calibrationId);

  return { ok: true, last: nowIso, next: nextIso, calibrationId, instrumentId };
}

// Guarda la URL (o ruta) del certificado en la calibración
export async function setCalibrationCertificateUrl(calibrationId, url) {
  await ensureUser();
  if (!calibrationId || !url) throw new Error('Falta calibrationId o url');

  const { error } = await supabase
    .from('calibrations')
    .update({
      certificate_url: url,
      certificate_created_at: new Date().toISOString(),
    })
    .eq('id', calibrationId);

  if (error) throw error;
  return { ok: true };
}

// ───────────────────────────────────────────────────────────────────────────────
// Measurements
// ───────────────────────────────────────────────────────────────────────────────

export async function addMeasurement(row) {
  await ensureUser();
  const { data, error } = await supabase
    .from('measurements')
    .insert(row)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

// Alias de compatibilidad
export const createMeasurement = addMeasurement;

export async function upsertMeasurement(row /* con id opcional */) {
  await ensureUser();
  const { data, error } = await supabase
    .from('measurements')
    .upsert(row)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function listMeasurements(calibrationId) {
  await ensureUser();
  const { data, error } = await supabase
    .from('measurements')
    .select('*')
    .eq('calibration_id', calibrationId)
    .order('setpoint', { ascending: true });

  if (error) throw error;

  // añade campos derivados (abs_error, rel_error, U estimada simple)
  return (data || []).map((m) => {
    const { abs_error, rel_error } = computeErrors(m);
    const u_std = [
      m.ref_std_unc, m.repeatability_std, m.resolution, m.drift_std, m.other_std
    ].filter(v => v != null).map(Number);
    const sumSquares = u_std.reduce((s, v) => s + v * v, 0);
    const u_c = Math.sqrt(sumSquares || 0);
    const coverage_k = Number(m.coverage_k ?? 2);
    const U = u_c * coverage_k;
    return { ...m, abs_error, rel_error, u: U };
  });
}

/** Elimina una medición por id */
export async function deleteMeasurement(id) {
  await ensureUser();
  const { error } = await supabase.from('measurements').delete().eq('id', id);
  if (error) throw error;
  return { ok: true };
}

// ───────────────────────────────────────────────────────────────────────────────
// Dashboards – Operations (Chart + Grid)
// ───────────────────────────────────────────────────────────────────────────────
//
// Ahora acepta filtro por cliente, usa fallback de vencimiento, PRIORIDAD real, y
// corrige el "En curso": solo se muestra si HAY una calibración abierta (status != 'closed').

export async function listActiveWorkOrders({ clientId } = {}) {
  const user = await ensureUser();

  let q = supabase
    .from('instruments')
    .select(`
      id, tag, brand, model, serial,
      nextcalibrationdate, lastcalibrationdate, calibrationintervalmonths,
      client_id,
      client:clients!instruments_client_id_fkey ( id, name )
    `)
    .eq('owner_id', user.id)
    .order('nextcalibrationdate', { ascending: true });

  if (clientId && clientId !== 'all') q = q.eq('client_id', clientId);

  const { data: inst, error: e1 } = await q;
  if (e1) throw e1;
  if (!inst?.length) return [];

  const ids = inst.map(i => i.id);

  // Traemos calibraciones con status y priority
  const { data: cals, error: e2 } = await supabase
    .from('calibrations')
    .select('id, instrument_id, date, technician_id, status, priority')
    .in('instrument_id', ids)
    .order('date', { ascending: false });
  if (e2) throw e2;

  // última calibración por instrumento + si hay alguna abierta
  const lastCal = new Map();
  const hasOpen = new Map();
  (cals || []).forEach(c => {
    const cur = lastCal.get(c.instrument_id);
    if (!cur || new Date(c.date) > new Date(cur.date)) lastCal.set(c.instrument_id, c);
    if ((c.status || 'in-progress') !== 'closed') hasOpen.set(c.instrument_id, true);
  });

  // Derivar estado correcto
  const now = new Date();
  const deriveStatus = (instrument) => {
    const open = hasOpen.get(instrument.id) || false;

    // Si hay calibración abierta → En curso
    if (open) return { status: 'in-progress', label: 'En curso' };

    // Sin abierta: derivar por fechas (Al día / Vencida / —)
    const dueISO = instrument.nextcalibrationdate
      || (instrument.lastcalibrationdate && instrument.calibrationintervalmonths
          ? addMonths(instrument.lastcalibrationdate, instrument.calibrationintervalmonths)
          : null);

    if (!dueISO) return { status: 'pending', label: 'Pendiente' };

    const due = new Date(dueISO);
    if (due < now) return { status: 'overdue', label: 'Vencida' };
    return { status: 'ok', label: 'Al día' };
  };

  // Construir filas
  const rows = (inst || []).map((i, idx) => {
    const lc = lastCal.get(i.id) || null;
    const fallbackDue = (!i.nextcalibrationdate && i.lastcalibrationdate && i.calibrationintervalmonths)
      ? addMonths(i.lastcalibrationdate, i.calibrationintervalmonths)
      : null;

    const st = deriveStatus(i);
    const instrumentName = [i.brand, i.model].filter(Boolean).join(' ') || i.tag || 'Instrumento';

    return {
      id: lc?.id || i.id,                 // compat
      calibrationId: lc?.id || null,      // id explícito
      instrumentId: i.id,
      orderNumber: String(idx + 1).padStart(4, '0'),
      client: i.client?.name ?? '—',
      instrument: instrumentName,
      serialNumber: i.serial || '—',
      status: st.status,
      statusLabel: st.label,
      priority: lc?.priority || 'low',    // prioridad real
      dueDate: i.nextcalibrationdate || fallbackDue || null,
      technician: lc?.technician_id ? 'Técnico' : '—',
      startDate: lc?.date || i.lastcalibrationdate || null,
    };
  });

  return rows;
}

/**
 * Serie mensual/semanal:
 * - workOrders: # calibraciones registradas
 * - avgTurnaround: (nextcalibrationdate - date) días cuando se puede inferir
 * Acepta filtro por clientId (y siempre filtra por owner).
 */
export async function getWorkOrdersSeries({ groupBy = 'month', months = 6, clientId } = {}) {
  const user = await ensureUser();
  const gb = groupBy === 'week' ? 'week' : 'month';

  const from = new Date();
  from.setMonth(from.getMonth() - months);

  // Determinar qué instrumentos considerar (del owner y, si aplica, del cliente)
  let instIds = [];
  {
    let iq = supabase
      .from('instruments')
      .select('id, nextcalibrationdate')
      .eq('owner_id', user.id);
    if (clientId && clientId !== 'all') iq = iq.eq('client_id', clientId);
    const { data: instList, error: e0 } = await iq;
    if (e0) throw e0;
    instIds = (instList || []).map(r => r.id);
    if (!instIds.length) return []; // sin instrumentos → serie vacía
  }

  // Calibraciones sólo de esos instrumentos
  const { data, error } = await supabase
    .from('calibrations')
    .select(`
      id, date, instrument_id,
      instrument:instruments ( id, nextcalibrationdate )
    `)
    .in('instrument_id', instIds)
    .gte('date', from.toISOString());
  if (error) throw error;

  const buckets = new Map(); // key → { count, tSum, tN }
  (data || []).forEach((c) => {
    const key = bucketKey(c.date || new Date(), gb);
    const b = buckets.get(key) || { count: 0, tSum: 0, tN: 0 };
    b.count += 1;

    const next = c.instrument?.nextcalibrationdate ? new Date(c.instrument.nextcalibrationdate) : null;
    const date = c.date ? new Date(c.date) : null;
    if (next && date) {
      const td = (next - date) / 86400000;
      if (Number.isFinite(td)) { b.tSum += Math.max(0, td); b.tN += 1; }
    }
    buckets.set(key, b);
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, v]) => ({
      period,
      workOrders: v.count,
      avgTurnaround: v.tN ? Math.round(((v.tSum / v.tN) + Number.EPSILON) * 10) / 10 : 0
    }));
}

// ───────────────────────────────────────────────────────────────────────────────
// Client Analytics
// ───────────────────────────────────────────────────────────────────────────────

export async function getTopClientsMetrics({ from, to, limit = 10, tariffPerCalibration } = {}) {
  await ensureUser();

  const toDate = to ? new Date(to) : new Date();
  const fromDate = from ? new Date(from) : new Date(Date.now() - 90 * 86400000);

  const { data, error } = await supabase
    .from('calibrations')
    .select(`
      id, date,
      instrument:instruments ( id, client_id, client:clients!instruments_client_id_fkey ( id, name ) )
    `)
    .gte('date', fromDate.toISOString())
    .lte('date', toDate.toISOString());
  if (error) throw error;

  const agg = new Map(); // clientId → { id, name, count, lastActivity }
  (data || []).forEach((row) => {
    const cid = row?.instrument?.client_id;
    const name = row?.instrument?.client?.name ?? '—';
    if (!cid) return;
    const a = agg.get(cid) || { id: cid, name, count: 0, lastActivity: null };
    a.count += 1;
    if (!a.lastActivity || new Date(row.date) > new Date(a.lastActivity)) a.lastActivity = row.date;
    agg.set(cid, a);
  });

  const total = Array.from(agg.values()).reduce((s, v) => s + v.count, 0) || 1;
  let rows = Array.from(agg.values()).map(v => ({
    id: v.id,
    name: v.name,
    calibrations: v.count,
    share: Math.round((v.count / total) * 100),
    growth: 0,
    lastActivity: v.lastActivity,
    revenue: typeof tariffPerCalibration === 'number' ? v.count * tariffPerCalibration : null
  }));

  rows.sort((a, b) => (b.calibrations - a.calibrations) || a.name.localeCompare(b.name));
  rows = rows.slice(limit ? 0 : 0, limit || rows.length);
  return rows;
}

export async function getClientPortfolioData({ from, to, tariffPerCalibration } = {}) {
  await ensureUser();

  const toDate = to ? new Date(to) : new Date();
  const fromDate = from ? new Date(from) : new Date(Date.now() - 90 * 86400000);

  // 1) instrumentos por cliente (filtrados por owner)
  const user = await ensureUser();
  const { data: inst, error: e1 } = await supabase
    .from('instruments')
    .select('id, client_id, nextcalibrationdate, client:clients!instruments_client_id_fkey ( id, name )')
    .eq('owner_id', user.id);
  if (e1) throw e1;

  const byClient = new Map(); // cid → { name, instruments:[], overdueCount }
  (inst || []).forEach(i => {
    const cid = i.client_id;
    if (!cid) return;
    const rec = byClient.get(cid) || { id: cid, name: i.client?.name ?? '—', instruments: [], overdue: 0 };
    rec.instruments.push(i);
    if (i.nextcalibrationdate && new Date(i.nextcalibrationdate) < new Date()) rec.overdue += 1;
    byClient.set(cid, rec);
  });

  // 2) calibraciones por cliente en el rango
  const { data: cals, error: e2 } = await supabase
    .from('calibrations')
    .select('id, date, instrument:instruments ( id, client_id )')
    .gte('date', fromDate.toISOString())
    .lte('date', toDate.toISOString());
  if (e2) throw e2;

  const calCount = new Map(); // cid → { count, lastActivity }
  (cals || []).forEach(c => {
    const cid = c.instrument?.client_id;
    if (!cid) return;
    const rec = calCount.get(cid) || { count: 0, lastActivity: null };
    rec.count += 1;
    if (!rec.lastActivity || new Date(c.date) > new Date(rec.lastActivity)) rec.lastActivity = c.date;
    calCount.set(cid, rec);
  });

  // 3) salida
  let out = Array.from(byClient.values()).map(({ id, name, instruments, overdue }) => {
    const totalInst = instruments.length || 1;
    const overdueRatio = Math.round((overdue / totalInst) * 100);
    const okRatio = 100 - overdueRatio;
    const cal = calCount.get(id);
    const count = cal?.count || 0;
    const revenue = typeof tariffPerCalibration === 'number' ? count * tariffPerCalibration : null;
    return {
      id,
      name,
      calibrations: count,
      onTimeRatio: okRatio,
      overdueRatio,
      revenue,
      lastActivity: cal?.lastActivity || null,
      instruments: totalInst
    };
  });

  out.sort((a, b) => (b.calibrations - a.calibrations) || a.name.localeCompare(b.name));
  return out;
}

export async function getRevenueSeries({
  groupBy = 'month',
  months = 12,
  tariffBase = 100,
} = {}) {
  await ensureUser();
  const gb = groupBy === 'week' ? 'week' : 'month';

  const from = new Date();
  from.setMonth(from.getMonth() - months);

  const { data, error } = await supabase
    .from('calibrations')
    .select('id, date')
    .gte('date', from.toISOString());
  if (error) throw error;

  const buckets = new Map(); // period → { low, medium, high, total }
  (data || []).forEach((c) => {
    const period = bucketKey(c.date || new Date(), gb);
    const b = buckets.get(period) || { low: 0, medium: 0, high: 0, total: 0 };
    b.low += tariffBase;   // todo a 'low'
    b.total += tariffBase;
    buckets.set(period, b);
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, v]) => ({
      period,
      total: Math.round(v.total),
      low: Math.round(v.low),
      medium: 0,
      high: 0,
    }));
}

// ───────────────────────────────────────────────────────────────────────────────
// Quality Compliance
// ───────────────────────────────────────────────────────────────────────────────

export async function getComplianceSeries({ period = '30d' } = {}) {
  await ensureUser();

  let days = 30, mode = 'week';
  if (period === '7d') { days = 7; mode = 'day'; }
  else if (period === '30d') { days = 30; mode = 'week'; }
  else if (period === '90d') { days = 90; mode = 'week'; }
  else if (period === '1y') { days = 365; mode = 'month'; }

  const from = new Date(Date.now() - days * 86400000);

  // Calibraciones + instrumento (para compliance)
  const { data: cals, error: e1 } = await supabase
    .from('calibrations')
    .select('id, date, instrument:instruments ( id, nextcalibrationdate )')
    .gte('date', from.toISOString());
  if (e1) throw e1;

  const calIds = (cals || []).map(c => c.id);

  // Mediciones (para accuracy)
  let meas = [];
  if (calIds.length) {
    const { data: m, error: e2 } = await supabase
      .from('measurements')
      .select('id, calibration_id, setpoint, reading')
      .in('calibration_id', calIds);
    if (e2) throw e2;
    meas = m || [];
  }

  // Buckets compliance
  const cBuckets = new Map(); // key → { total, ok }
  (cals || []).forEach(c => {
    const key = bucketKey(c.date || new Date(), mode);
    const b = cBuckets.get(key) || { total: 0, ok: 0 };
    b.total += 1;
    const nextOk = c.instrument?.nextcalibrationdate && c.date
      ? new Date(c.instrument.nextcalibrationdate) >= new Date(c.date)
      : false;
    if (nextOk) b.ok += 1;
    cBuckets.set(key, b);
  });

  // Map calId → key
  const calToKey = new Map();
  (cals || []).forEach(c => calToKey.set(c.id, bucketKey(c.date || new Date(), mode)));

  // Buckets accuracy
  const aBuckets = new Map(); // key → { total, ok }
  meas.forEach(m => {
    const key = calToKey.get(m.calibration_id) || bucketKey(new Date(), mode);
    const b = aBuckets.get(key) || { total: 0, ok: 0 };
    const { rel_error } = computeErrors(m);
    const ok = rel_error != null ? Math.abs(rel_error) <= 0.01 : false;
    b.total += 1;
    if (ok) b.ok += 1;
    aBuckets.set(key, b);
  });

  const keys = new Set([...cBuckets.keys(), ...aBuckets.keys()]);
  return Array.from(keys).sort((a, b) => a.localeCompare(b)).map(k => {
    const c = cBuckets.get(k) || { total: 0, ok: 0 };
    const a = aBuckets.get(k) || { total: 0, ok: 0 };
    const compliance = c.total ? Math.round((c.ok / c.total) * 100) : 0;
    const accuracy   = a.total ? Math.round((a.ok / a.total) * 100) : 0;
    const quality10  = (0.6 * accuracy + 0.4 * compliance) / 10; // 0..10
    const quality    = Math.round((quality10 + Number.EPSILON) * 10) / 10;
    return { period: k, accuracy, compliance, quality };
  });
}

export async function getComplianceGridRows({ days = 365 } = {}) {
  const user = await ensureUser();

  const since = new Date(Date.now() - days * 86400000).toISOString();

  // 1) instrumentos (filtrados por owner)
  const { data: instruments, error: e1 } = await supabase
    .from('instruments')
    .select('id, tag, brand, model, type, unit, serial, nextcalibrationdate, lastcalibrationdate, calibrationintervalmonths, owner_id')
    .eq('owner_id', user.id)
    .order('nextcalibrationdate', { ascending: true });
  if (e1) throw e1;

  if (!instruments?.length) return [];

  const ids = instruments.map(i => i.id);

  // 2) última calibración por instrumento
  const { data: cals, error: e2 } = await supabase
    .from('calibrations')
    .select('id, instrument_id, date, technician_id')
    .in('instrument_id', ids)
    .gte('date', since)
    .order('date', { ascending: false });
  if (e2) throw e2;

  const latestCal = new Map();
  (cals || []).forEach(c => {
    const cur = latestCal.get(c.instrument_id);
    if (!cur || new Date(c.date) > new Date(cur.date)) latestCal.set(c.instrument_id, c);
  });

  // 3) mediciones de la última calibración
  const lastCalIds = Array.from(latestCal.values()).map(c => c.id);
  let meas = [];
  if (lastCalIds.length) {
    const { data: m, error: e3 } = await supabase
      .from('measurements')
      .select('id, calibration_id, setpoint, reading')
      .in('calibration_id', lastCalIds);
    if (e3) throw e3;
    meas = m || [];
  }
  const byCal = new Map();
  meas.forEach(m => {
    const arr = byCal.get(m.calibration_id) || [];
    arr.push(m);
    byCal.set(m.calibration_id, arr);
  });

  // 4) construir filas
  const rows = instruments.map(inst => {
    const cal = latestCal.get(inst.id) || null;
    const ms = cal ? (byCal.get(cal.id) || []) : [];

    // precisión %: |rel_error| ≤ 1%
    let accPct = null;
    if (ms.length) {
      const ok = ms.reduce((s, m) => {
        const { rel_error } = computeErrors(m);
        return s + (rel_error != null && Math.abs(rel_error) <= 0.01 ? 1 : 0);
      }, 0);
      accPct = Math.round((ok / ms.length) * 100);
    }

    const fallbackDue = (!inst.nextcalibrationdate && inst.lastcalibrationdate && inst.calibrationintervalmonths)
      ? addMonths(inst.lastcalibrationdate, inst.calibrationintervalmonths)
      : null;

    const st = deriveStatusFromDates(inst.nextcalibrationdate || fallbackDue);
    const instrumentName = [inst.brand, inst.model].filter(Boolean).join(' ') || inst.tag || 'Instrumento';

    return {
      id: inst.id,
      instrument: instrumentName,
      type: inst.type || '—',
      certification: 'ISO/IEC 17025',
      status: st.status,
      lastCalibration: cal?.date || inst.lastcalibrationdate || null,
      nextDue: inst.nextcalibrationdate || fallbackDue || null,
      accuracy: accPct,
      technician: cal?.technician_id ? 'Técnico' : '—',
      location: '—',
      serial: inst.serial || '—',
      tag: inst.tag || '—',
    };
  });

  rows.sort((a, b) => {
    const ad = rowsDateOrInfinity(a.nextDue);
    const bd = rowsDateOrInfinity(b.nextDue);
    if (ad.getTime() !== bd.getTime()) return ad - bd;
    return (a.instrument || '').localeCompare(b.instrument || '');
  });

  return rows;
}

function rowsDateOrInfinity(val) {
  if (!val) return new Date(8640000000000000); // max date
  const d = new Date(val);
  return isNaN(d.getTime()) ? new Date(8640000000000000) : d;
}
